import { useCallback, useEffect, useState } from "react";
import OrganisationLayout from "../layout/OrganisationLayout";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import ConfirmModal from "../../modals/ConfirmModal";
import StudentsImagesModal from "../../modals/StudentsImagesModal";
import { GROUP_ROLES, getOrganisationRoleAccess } from "../utils/roleAccess";
import {
  organisationCreateMemberAction,
  organisationDeleteMemberAction,
  organisationMemberDetailsAction,
  organisationGroupStaffListAction,
  organisationUpdateMemberAction,
} from "../../Redux/Action/organisationAction";

const DEFAULT_FORM = {
  groupRole: GROUP_ROLES.COLLABORATOR,
  title: "",
  firstName: "",
  surName: "",
  email: "",
  phoneNumber: "",
  profileImage: "",
};

const MEMBER_ROLE_OPTIONS = [
  // { label: "Group Super Admin", value: GROUP_ROLES.SUPER_ADMIN },
  { label: "Group Admin", value: GROUP_ROLES.ADMIN },
  { label: "Group Collaborator", value: GROUP_ROLES.COLLABORATOR },
  // { label: "No Permissions", value: GROUP_ROLES.NO_PERMISSIONS },
];

const TITLE_OPTIONS = ["Mr.", "Mrs.", "Miss", "Ms.", "Dr."];

export const formatDisplayDate = (value) => {
  if (!value) return "-";

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDisplayDateTime = (value) => {
  if (!value) return "Never";

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatMemberRole = (role) => {
  if (!role) return "-";

  return role
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getMemberId = (member) =>
  member?._id || member?.memberId || member?.id || member?.userId || "";

export const getMemberGroupRole = (member) =>
  member?.groupRole || member?.organisationGroupMemberships?.[0]?.groupRole || "";

const getMemberPayload = (payload) => {
  return (
    payload?.member ||
    payload?.memberDetails ||
    payload?.details ||
    payload?.data ||
    payload ||
    {}
  );
};

const getMembersList = (payload) => {
  const list =
    payload?.staff ||
    payload?.members ||
    payload?.memberList ||
    payload?.rows ||
    payload?.docs ||
    payload?.data ||
    [];

  return Array.isArray(list) ? list : [];
};

const getMembersMeta = (payload, fallbackPage, fallbackLimit) => {
  const total = Number(payload?.total || payload?.count || 0);
  const limit = Number(payload?.limit || fallbackLimit || 10);
  const totalPages =
    Number(payload?.totalPages) || Math.ceil(total / (limit || 10)) || 0;

  return {
    total,
    page: Number.isFinite(Number(payload?.page))
      ? Number(payload?.page)
      : fallbackPage,
    limit,
    totalPages,
  };
};

const getFullName = (member) => {
  const name = [member?.title, member?.firstName, member?.surName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || member?.name || member?.fullName || "-";
};

const getProfileImage = (member) =>
  member?.profileImage ||
  member?.image ||
  member?.avatar ||
  member?.profile ||
  "";

export const getMemberStatus = (member) => {
  if (member?.status) return member.status;
  if (member?.isDeleted) return "Deleted";
  if (member?.isSuspended) return "Suspended";
  return "Active";
};

const getOrganisationGroupName = (member) =>
  member?.organisationGroupId?.name ||
  member?.organisationGroupName ||
  member?.organisationGroup ||
  "-";

const appendIfPresent = (payload, key, value) => {
  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (trimmedValue) {
      payload[key] = trimmedValue;
    }

    return payload;
  }

  if (value !== undefined && value !== null && value !== "") {
    payload[key] = value;
  }

  return payload;
};

const buildCreateMemberPayload = (formData) => {
  const payload = {
    groupRole: (formData?.groupRole || "").trim(),
    firstName: (formData?.firstName || "").trim(),
    email: (formData?.email || "").trim(),
  };

  appendIfPresent(payload, "title", formData?.title);
  appendIfPresent(payload, "surName", formData?.surName);
  appendIfPresent(payload, "phoneNumber", formData?.phoneNumber);
  appendIfPresent(payload, "profileImage", formData?.profileImage);

  return payload;
};

const normaliseFormValues = (member) => {
  let titleVal = (member?.title || "").trim();

  if (titleVal && !TITLE_OPTIONS.includes(titleVal)) {
    const match = TITLE_OPTIONS.find(
      (opt) => opt.replace(".", "").toLowerCase() === titleVal.replace(".", "").toLowerCase()
    );
    if (match) {
      titleVal = match;
    }
  }

  return {
    groupRole: getMemberGroupRole(member) || member?.role || DEFAULT_FORM.groupRole,
    title: titleVal,
    firstName: member?.firstName || "",
    surName: member?.surName || "",
    email: member?.email || "",
    phoneNumber: member?.phoneNumber || "",
    profileImage: getProfileImage(member),
  };
};

export function OrganisationMemberFormModal({
  show,
  isLoading,
  mode,
  member,
  onHide,
  onSubmit,
}) {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [slectedImage, setSlectedImage] = useState(null);
  const [studentModalShow, setStudentModalShow] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData(member ? normaliseFormValues(member) : DEFAULT_FORM);
      setSlectedImage(null);
    }
  }, [member, show]);

  const handleChange = ({ target: { name, value } }) => {
    console.log(name  , "name");
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = { ...formData };

    if (slectedImage) {
      payload.profileImage = slectedImage;
    } else if (!payload.profileImage) {
      delete payload.profileImage;
    }

    onSubmit(payload);
  };

  const handleOpenStudentModal = () => {
    setStudentModalShow(true);
  };

  const handleCloseStudentModal = () => {
    setStudentModalShow(false);
  };

  return (
    <>
      <Modal
        size="lg"
        show={show}
        onHide={onHide}
        className="comn-modal-set create"
        centered
      >
        <Modal.Header className="d-block" style={{ border: 0 }}>
          <Row>
            <Col lg={12} className="d-flex justify-content-center mb-4">
              <div>
                <Modal.Title className="p-0">
                  {mode === "edit"
                    ? "Edit Member"
                    : "Create Organisation Member"}
                </Modal.Title>
                <p className="text-center mb-0">
                  {mode === "edit"
                    ? "Update the selected organisation member."
                    : "Add a new member to this organisation group."}
                </p>
              </div>
            </Col>
          </Row>
          <div className="modal-right">
            <button className="menu-dots" onClick={onHide}>
              <img
                src={require("../../Assets/Images/dotsweb.svg").default}
                alt="close"
              />
            </button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Profile (optional)</Form.Label>
              <div className="upld-vd-box mt-4 mb-4">
                <div className="upld-img-side">
                  {slectedImage || formData.profileImage ? (
                    <div>
                      <img
                        src={slectedImage || formData.profileImage}
                        className="img-fluid"
                        alt="Members graphic"
                      />
                    </div>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="80"
                        height="80"
                        viewBox="0 0 80 80"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_48_1806)">
                          <path
                            d="M33.3337 53.3333H46.667C48.5003 53.3333 50.0003 51.8333 50.0003 50V33.3333H55.3003C58.267 33.3333 59.767 29.7333 57.667 27.6333L42.367 12.3333C41.067 11.0333 38.967 11.0333 37.667 12.3333L22.367 27.6333C20.267 29.7333 21.7337 33.3333 24.7003 33.3333H30.0003V50C30.0003 51.8333 31.5003 53.3333 33.3337 53.3333ZM20.0003 60H60.0003C61.8337 60 63.3337 61.5 63.3337 63.3333C63.3337 65.1667 61.8337 66.6667 60.0003 66.6667H20.0003C18.167 66.6667 16.667 65.1667 16.667 63.3333C16.667 61.5 18.167 60 20.0003 60Z"
                            fill="#d200a5"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_48_1806">
                            <rect width="80" height="80" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <div className="upld-text">
                        <h3> Update Photo</h3>
                      </div>
                    </>
                  )}
                </div>
                <div className="upld-btn-side">
                  <h2>Select</h2>
                  <p>550 x 1200 recommended</p>
                  <div className="upload mt-5" onClick={handleOpenStudentModal}>
                    <Link to="">Update Photo</Link>
                  </div>
                </div>
              </div>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  {((formData.groupRole || "").toLowerCase().includes("super") || (member?.role || "").toLowerCase().includes("super") || (member?.accountType || "").toLowerCase().includes("super")) ? (
                    <Form.Control
                      type="text"
                      className="input-set"
                      value={formatMemberRole(formData.groupRole)}
                      disabled
                    />
                  ) : (
                    <Form.Select
                      className="input-set"
                      name="groupRole"
                      value={formData.groupRole}
                      onChange={handleChange}
                    >
                      {MEMBER_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Select
                    className="input-set"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                  >
                    <option value="">Select Title</option>
                    {TITLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-set"
                    placeholder="Enter first name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Surname</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-set"
                    placeholder="Enter surname"
                    name="surName"
                    value={formData.surName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    className="input-set"
                    placeholder="Enter email address"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-set"
                    placeholder="Enter phone number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Modal.Footer className="border-none justify-content-center">
              <Button
                variant="primary"
                className="modal-f-btn"
                type="submit"
                disabled={isLoading}
              >
                {isLoading
                  ? mode === "edit"
                    ? "Updating..."
                    : "Creating..."
                  : mode === "edit"
                    ? "Update"
                    : "Create"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <StudentsImagesModal
        studentModalshow={studentModalShow}
        handleCloseStudentModal={handleCloseStudentModal}
        setSlectedImage={setSlectedImage}
      />
    </>
  );
}

export function OrganisationMemberDetailsModal({ show, member, isLoading, onHide }) {
  const details = member || {};
  const profileImage = getProfileImage(details);

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      className="comn-modal-set create"
      centered
    >
      <Modal.Header className="d-block" style={{ border: 0 }}>
        <Row>
          <Col lg={12} className="d-flex justify-content-center mb-4">
            <div>
              <Modal.Title className="p-0">Member Details</Modal.Title>
              <p className="text-center mb-0">
                Review the selected organisation member.
              </p>
            </div>
          </Col>
        </Row>
        <div className="modal-right">
          <button className="menu-dots" onClick={onHide}>
            <img
              src={require("../../Assets/Images/dotsweb.svg").default}
              alt="close"
            />
          </button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">Loading member details...</div>
        ) : (
          <>
            <div className="organisation-member-summary">
              <img
                className="organisation-member-avatar"
                src={profileImage || require("../../Assets/Images/girl.png")}
                alt={getFullName(details)}
              />
              <div>
                <h3>{getFullName(details)}</h3>
                <p>{formatMemberRole(details?.role)}</p>
              </div>
            </div>
            <div className="organisation-member-detail-grid">
              <div className="organisation-member-detail-item">
                <span>Email</span>
                <strong>{details?.email || "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Phone Number</span>
                <strong>{details?.phoneNumber || "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Organisation Group</span>
                <strong>{getOrganisationGroupName(details)}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Last Logged In</span>
                <strong>{formatDisplayDateTime(details?.lastLoggedIn)}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Platform Visits</span>
                <strong>{details?.platformVisits ?? 0}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Status</span>
                <strong>{getMemberStatus(details)}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Added On</span>
                <strong>
                  {formatDisplayDate(
                    details?.createdAt || details?.joinedAt || details?.date,
                  )}
                </strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Last Updated</span>
                <strong>{formatDisplayDateTime(details?.updatedAt)}</strong>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function OrganisationGroupsMembers() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const access = getOrganisationRoleAccess(userData);
  const canManageMembers = access.canInviteMemberOrganisation;

  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [tableMeta, setTableMeta] = useState({
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailMember, setDetailMember] = useState(null);
  const [deleteMemberData, setDeleteMemberData] = useState(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [activeDeleteId, setActiveDeleteId] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const fetchMembers = useCallback(
    async (
      nextPage = page,
      nextSearch = searchValue,
      nextRole = selectedRole,
      nextStatus = selectedStatus,
      nextSortBy = sortBy,
      nextSortOrder = sortOrder,
    ) => {
      setIsLoading(true);

      const response = await dispatch(
        organisationGroupStaffListAction({
          page: nextPage,
          limit: tableMeta.limit || 10,
          search: nextSearch,
          ogMemberRole: nextRole || "group_admin,group_collaborator",
          ...(nextStatus && { status: nextStatus }),
          sortBy: nextSortBy,
          sortOrder: nextSortOrder,
        }),
      );

      console.log("organisationGroupStaffListAction response", response?.payload);

      if (response?.error) {
        setTableData([]);
        setTableMeta({
          total: 0,
          page: nextPage,
          limit: tableMeta.limit || 10,
          totalPages: 0,
        });
        toast.error(
          response?.payload?.message || "Unable to load organisation members.",
        );
        setIsLoading(false);
        return;
      }

      const payload = response?.payload?.data || response?.payload || {};
      setTableData(getMembersList(payload));
      setTableMeta(getMembersMeta(payload, nextPage, tableMeta.limit || 10));
      setIsLoading(false);
    },
    [dispatch, page, searchValue, tableMeta.limit, selectedRole, selectedStatus, sortBy, sortOrder],
  );

  useEffect(() => {
    fetchMembers(page, searchValue, selectedRole, selectedStatus, sortBy, sortOrder);
  }, [fetchMembers, page, searchValue, selectedRole, selectedStatus, sortBy, sortOrder]);

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortBy(field);
    setSortOrder(isAsc ? "desc" : "asc");
    setPage(0);
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    setPage(0);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
    setPage(0);
  };

  const handleOpenCreateModal = () => {
    setFormMode("create");
    setSelectedMember(null);
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setSelectedMember(null);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailMember(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteMemberData(null);
  };

  const loadMemberDetails = async (member) => {
    const memberId = getMemberId(member);

    if (!memberId) {
      return member;
    }

    let accountType = member?.accountType || "";

    const response = await dispatch(organisationMemberDetailsAction({
      memberId,
      accountType,
    }));

    console.log("organisationMemberDetailsAction response", response?.payload);

    if (response?.error) {
      toast.error(
        response?.payload?.message ||
        "Unable to load member details right now.",
      );
      return member;
    }

    const payload = response?.payload?.data || response?.payload || {};
    const details = getMemberPayload(payload);
    return {
      ...member,
      ...details,
      phoneNumber: details?.phoneNumber || member?.phoneNumber || "",
    };
  };

  const handleViewDetails = async (member) => {
    setShowDetailsModal(true);
    setIsDetailLoading(true);
    setDetailMember(member);
    const detailedMember = await loadMemberDetails(member);
    setDetailMember(detailedMember);
    setIsDetailLoading(false);
  };

  const handleEditMember = async (member) => {
    setFormMode("edit");
    setShowFormModal(true);
    setSelectedMember(member);
    const detailedMember = await loadMemberDetails(member);
    setSelectedMember(detailedMember);
  };

  const handleSubmitMember = async (formData) => {
    setIsFormSubmitting(true);

    const payload = buildCreateMemberPayload(formData);

    let accountType = selectedMember?.accountType || "";

    if (accountType) {
      payload.accountType = accountType;
    }

    const action =
      formMode === "edit"
        ? organisationUpdateMemberAction({
          memberId: getMemberId(selectedMember),
          ...payload,
        })
        : organisationCreateMemberAction(payload);

    const response = await dispatch(action);

    console.log(
      formMode === "edit"
        ? "organisationUpdateMemberAction response"
        : "organisationCreateMemberAction response",
      response?.payload,
    );

    if (response?.error) {
      toast.error(
        response?.payload?.message ||
        `Unable to ${formMode === "edit" ? "update" : "create"} member.`,
      );
      setIsFormSubmitting(false);
      return;
    }

    toast.success(
      response?.payload?.message ||
      response?.payload?.data?.message ||
      `Member ${formMode === "edit" ? "updated" : "created"} successfully.`,
    );
    handleCloseFormModal();
    setIsFormSubmitting(false);
    fetchMembers(page, searchValue, selectedRole, selectedStatus, sortBy, sortOrder);
  };

  const handleDeleteMember = async (member) => {
    setDeleteMemberData(member);
    setShowDeleteModal(true);
  };

  const confirmDeleteMember = async () => {
    const member = deleteMemberData;
    const memberId = getMemberId(member);

    if (!memberId) {
      console.log("organisationDeleteMemberAction skipped", member);
      handleCloseDeleteModal();
      return;
    }

    let accountType = member?.accountType || "";
    const memberGroupRole = getMemberGroupRole(member);
    if (memberGroupRole === "collaborator" || memberGroupRole === "group_collaborator") accountType = "Group Collaborator";
    else if (memberGroupRole === "admin" || memberGroupRole === "group_admin") accountType = "Group Admin";
    else if (memberGroupRole === "super_admin" || memberGroupRole === "group_super_admin") accountType = "Group Super Admin";

    setActiveDeleteId(memberId);
    handleCloseDeleteModal();
    const response = await dispatch(organisationDeleteMemberAction({
      memberId,
      accountType,
    }));

    console.log("organisationDeleteMemberAction response", response?.payload);

    if (response?.error) {
      toast.error(
        response?.payload?.message || "Unable to delete member right now.",
      );
      setActiveDeleteId("");
      return;
    }

    toast.success(
      response?.payload?.message ||
      response?.payload?.data?.message ||
      "Member deleted successfully.",
    );

    setActiveDeleteId("");

    const nextPage =
      tableData.length === 1 && page > 0 ? Math.max(page - 1, 0) : page;

    if (nextPage !== page) {
      setPage(nextPage);
    } else {
      fetchMembers(nextPage, searchValue, selectedRole, selectedStatus, sortBy, sortOrder);
    }
  };

  const totalPages = tableMeta.totalPages || 0;
  const pageStart = tableMeta.total === 0 ? 0 : page * tableMeta.limit + 1;
  const pageEnd = Math.min((page + 1) * tableMeta.limit, tableMeta.total);

  return (
    <OrganisationLayout>
      <Container fluid>
        <div className="heading-top mt-5 mb-2">
          {/* <p className="organization-crumb">Organisation Group</p> */}
          <h2>Organisation Group Accounts</h2>
        </div>

        <Row className="g-3 align-items-center mt-1">

          <Col xl={4} lg={5}>
            <div className="organisation-search-shell">
              <input
                type="text"
                placeholder="Search members"
                value={searchValue}
                onChange={handleSearchChange}
              />
            </div>
          </Col>
          <Col xl={2} lg={4}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Account Type"
                className="cmn-select"
                onChange={handleRoleChange}
                value={selectedRole}
              >
                <option value="">All Account Types</option>
                <option value={GROUP_ROLES.ADMIN}>Group Admin</option>
                <option value={GROUP_ROLES.COLLABORATOR}>
                  Group Collaborator
                </option>
              </Form.Select>
            </div>
          </Col>
          <Col xl={2} lg={4}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Status"
                className="cmn-select"
                onChange={handleStatusChange}
                value={selectedStatus}
              >
                <option value="">All Statuses</option>
                <option value="Created">Created</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Blocked</option>
              </Form.Select>
            </div>
          </Col>
          {canManageMembers && (
            <Col
              xl={4}
              lg={3}
              className="create-new-btn d-flex justify-content-end"
            >
              <button className="cmn-pink" onClick={handleOpenCreateModal}>
                Create User Account
              </button>
            </Col>
          )}
        </Row>

        {/* {!canManageMembers && (
          <div className="organisation-note-banner">
            Your role can view organisation members but cannot add or edit them.
          </div>
        )} */}

        <section className="organisation-groups-table-shell organisation-groups-table-shell-space">
          <div className="organisation-panel-heading">
            <div>
              <h3>User List</h3>
            </div>
          </div>

          <div className="organisation-table-responsive">
            <table>
              <thead>
                <tr>
                  <th>
                    <span
                      className={`cursor-pointer ${sortBy === "firstName" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("firstName")}
                    >
                      User
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${sortBy === "accountType" ? sortOrder : ""}`}
                      onClick={() => handleSort("accountType")}
                    >
                      Role
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${sortBy === "email" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </span>
                  </th>
                
                  <th>
                    <span
                      className={`cursor-pointer ${sortBy === "createdAt" ? sortOrder : ""}`}
                      onClick={() => handleSort("createdAt")}
                    >
                      Created At
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${sortBy === "joiningDate" ? sortOrder : ""}`}
                      onClick={() => handleSort("joiningDate")}
                    >
                      Joining Date
                    </span>
                  </th>
                     <th>
                    <span
                      className={`cursor-pointer ${sortBy === "lastLoggedIn" ? sortOrder : ""
                        }`}
                      onClick={() => handleSort("lastLoggedIn")}
                    >
                      Last Logged In
                    </span>
                  </th>
                    <th>
                    <span
                      className={`cursor-pointer ${sortBy === "platformVisits" ? sortOrder : ""}`}
                      onClick={() => handleSort("platformVisits")}
                    >
                      Platform Visits
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${sortBy === "collectionShareCount" ? sortOrder : ""}`}
                      onClick={() => handleSort("collectionShareCount")}
                    >
                    Collection Share Count
                    </span>
                  </th>
               
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={10} className="text-center py-5">
                      Loading members...
                    </td>
                  </tr>
                )}

                {!isLoading && tableData.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center py-5">
                      This Organisation Group has no direct users yet.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  tableData.map((row) => {
                    const memberId = getMemberId(row);

                    return (
                      <tr key={memberId || `${row?.email}-${row?.firstName}`}>
                        <td>
                          <div
                            className="organisation-person-cell cursor-pointer"
                            onClick={() => handleViewDetails(row)}
                          >
                            <img
                              className="organisation-avatar-sm"
                              src={
                                getProfileImage(row) ||
                                require("../../Assets/Images/girl.png")
                              }
                              alt={getFullName(row)}
                              height="42"
                              width="42"
                            />
                            <div className="organisation-name-cell">
                              <strong>{getFullName(row)}</strong>
                              <p>Added {formatDisplayDate(row?.createdAt)}</p>
                            </div>
                          </div>
                        </td>
                        <td>{formatMemberRole(getMemberGroupRole(row))}</td>
                        <td>{row?.email || "-"}</td>
                        <td>{formatDisplayDate(row?.createdAt)}</td>
                        <td>{formatDisplayDate(row?.joiningDate)}</td>
                        <td>{formatDisplayDateTime(row?.lastLoggedIn)}</td>
                        <td>{row?.platformVisits ?? 0}</td>
                        <td>{row?.collectionShareCount ?? 0}</td>
                        <td>
                          <span
                            className={`organisation-badge ${["suspended", "deleted", "blocked", "inactive"].includes(row?.status?.toLowerCase()) ||
                              row?.isSuspended ||
                              row?.isDeleted
                              ? "organisation-badge-muted"
                              : "organisation-badge-success"
                              }`}
                          >
                            {getMemberStatus(row)}
                          </span>
                        </td>
                        <td>
                          <div className="organisation-inline-actions">
                            {/* <button
                              className="organisation-inline-action"
                              onClick={() => handleViewDetails(row)}
                            >
                              Details
                            </button> */}
                            {canManageMembers && (
                              <button
                                className="organisation-inline-action"
                                onClick={() => handleEditMember(row)}
                              >
                                Edit
                              </button>
                            )}
                            {canManageMembers ? (
                              <button
                                className="organisation-inline-action text-danger"
                                onClick={() => handleDeleteMember(row)}
                                disabled={activeDeleteId === memberId}
                              >
                                {activeDeleteId === memberId
                                  ? "Deleting..."
                                  : "Delete"}
                              </button>
                            ) : (
                              <span className="organisation-inline-placeholder">
                                No actions
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="organisation-pagination">
            <div className="organisation-pagination-meta">
              Showing {pageStart}-{pageEnd} of {tableMeta.total || 0} users
            </div>
            <div className="organisation-pagination-actions">
              <button
                className="organisation-pagination-btn"
                onClick={() =>
                  setPage((prevState) => Math.max(prevState - 1, 0))
                }
                disabled={page === 0 || isLoading}
              >
                Previous
              </button>
              <span className="organisation-pagination-page">
                Page {page + 1}
                {totalPages > 0 ? ` of ${totalPages}` : ""}
              </span>
              <button
                className="organisation-pagination-btn"
                onClick={() => setPage((prevState) => prevState + 1)}
                disabled={
                  isLoading ||
                  tableData.length === 0 ||
                  (totalPages > 0 && page + 1 >= totalPages)
                }
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </Container>

      <OrganisationMemberFormModal
        show={showFormModal}
        isLoading={isFormSubmitting}
        mode={formMode}
        member={selectedMember}
        onHide={handleCloseFormModal}
        onSubmit={handleSubmitMember}
      />

      <OrganisationMemberDetailsModal
        show={showDetailsModal}
        member={detailMember}
        isLoading={isDetailLoading}
        onHide={handleCloseDetailsModal}
      />

      <ConfirmModal
        isModalOpen={showDeleteModal}
        handleConfirmClose={handleCloseDeleteModal}
        data={deleteMemberData}
        selectedType="deleteOrganisationMember"
        extraFunction={confirmDeleteMember}
        message={`Are you sure you want to delete ${deleteMemberData ? getFullName(deleteMemberData) : "this member"
          } ?`}
      />
    </OrganisationLayout>
  );
}
